import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useCallback, useEffect } from "react";
import { toast } from "react-toastify";
import { trainsErrorMsgs } from "../../errors";
import { useHistory, useLocation } from "react-router-dom";
import {
  trainSearchFormUpdate,
  trainSearchResultsFailed,
  trainSearchResultsIdle,
  trainSearchResultsLoading,
  trainSearchResultsUpdate,
} from "../../actions/search/trains_search";
import { changeServiceSessionIds } from "../../actions/search/session_ids";
import { getSavedSessionRequestTrains, searchRequestTrains } from "../../api/trains";
import { retrieveSearchSessionMetadata, storeSearchSessionMetadata } from "../../api";
import { DateTime } from "luxon";
import { useApoAuth } from "..";

function actionFailed({ history, onFailed, errorMsg }) {
  onFailed();
  toast.warning(errorMsg, {
    duration: 3000,
  });
  history.push("/");
}

function getTrainSearchPayload({ form, market_type, source_entity }) {
  var output = {
    market_type,
    source_entity,
    legs: [
      {
        origin: {
          type_: form.origin.type,
          code: form.origin.code,
        },
        destination: {
          type_: form.destination.type,
          code: form.destination.code,
        },
        departure: DateTime.fromISO(form.departDateTime, { zone: "utc" })
          .setZone("local")
          .toFormat("yyyy-MM-dd'T'HH:mm:ss"),
        direct_only: false,
      },
    ],
    traveler_ages: [...form.adults_ages, ...form.seniors_ages, ...form.youths_ages],
  };

  if (form.hasReturn) {
    output.legs.push({
      origin: {
        type_: form.destination.type,
        code: form.destination.code,
      },
      destination: {
        type_: form.origin.type,
        code: form.origin.code,
      },
      departure: DateTime.fromISO(form.returnDateTime, { zone: "utc" })
        .setZone("local")
        .toFormat("yyyy-MM-dd'T'HH:mm:ss"),

      direct_only: false,
    });
  }

  return output;
}

export function useTrainsInitSearch({ setLoadingSearchBtn = () => {} } = {}) {
  const dispatch = useDispatch();
  const history = useHistory();
  var { market_type, source_entity } = useApoAuth();

  const search_form = useSelector((state) => state.TrainSearchForm);

  const onFormUpdate = useCallback((form) => dispatch(trainSearchFormUpdate({ form })), [dispatch]);

  const onChangeSessionIDs = useCallback(
    (value) => dispatch(changeServiceSessionIds("TRA", value)),
    [dispatch],
  );

  const onSearchResultsLoading = useCallback(
    () => dispatch(trainSearchResultsLoading()),
    [dispatch],
  );

  const onSearchResultsIdle = useCallback(() => dispatch(trainSearchResultsIdle()), [dispatch]);

  const onFailed = useCallback(() => {
    dispatch(trainSearchResultsIdle());
    dispatch(trainSearchResultsFailed());
  }, [dispatch]);

  const onTrainSearchResultsUpdate = useCallback(
    ({ trains }) => {
      dispatch(trainSearchResultsUpdate({ trains }));
    },
    [dispatch],
  );

  async function initSearch(form) {
    if (_.isEmpty(form)) {
      return;
    }
    onSearchResultsLoading();

    const fullForm = { ...search_form, ...form };

    const payload = getTrainSearchPayload({
      form: fullForm,
      market_type,
      source_entity,
    });

    onFormUpdate({ ...fullForm, searchPayload: payload });

    const results = await searchRequestTrains({
      payload,
      onFailed: (errorMsg) => actionFailed({ history, onFailed, errorMsg }),
    });

    if (!results || !results.session_id) {
      setLoadingSearchBtn(false);

      return;
    }
    const search_session_id = _.get(results, "session_id", "");

    const searchSessionMetaResult = await storeSearchSessionMetadata({
      service_type: "TRA",
      session_data: {
        search_session_id,
        search_form: { ...fullForm, searchPayload: payload },
        searchPayload: { market_type, source_entity },
      },
    });

    if (
      _.isEmpty(_.get(searchSessionMetaResult, "session_data", {})) ||
      !searchSessionMetaResult.session_id
    ) {
      setLoadingSearchBtn(false);
      actionFailed({
        history,
        onFailed,
        errorMsg: trainsErrorMsgs.searchForm.no_session,
      });

      return;
    }
    const { session_id: page_session_id, website_api_key } = searchSessionMetaResult;
    onTrainSearchResultsUpdate({ trains: results.results, website_api_key });
    onChangeSessionIDs({ page_session_id, search_session_id });

    history.push(`/trains/${page_session_id}`);
    onSearchResultsIdle();
  }

  return { initSearch };
}

// ===============================================================

export function useGetTrainsSavedSearch({ search_session_id }) {
  const dispatch = useDispatch();
  const history = useHistory();

  const hasResults = useSelector((state) => state.TrainSearchResults.trains.length);

  const onSearchResultsLoading = useCallback(
    () => dispatch(trainSearchResultsLoading()),
    [dispatch],
  );

  const onSearchResultsIdle = useCallback(() => dispatch(trainSearchResultsIdle()), [dispatch]);

  const onFailed = useCallback(() => {
    dispatch(trainSearchResultsIdle());
    dispatch(trainSearchResultsFailed());
  }, [dispatch]);

  const onTrainSearchResultsUpdate = useCallback(
    ({ trains }) => {
      dispatch(trainSearchResultsUpdate({ trains }));
    },
    [dispatch],
  );

  async function getSavedSearch() {
    onSearchResultsLoading();

    const results = await getSavedSessionRequestTrains({
      search_session_id,
    });

    if (!results || !results.session_id) {
      actionFailed({
        history,
        onFailed,
        errorMsg: trainsErrorMsgs.searchForm.init_search,
      });
      return;
    }

    onTrainSearchResultsUpdate({ trains: results.results });
    onSearchResultsIdle();
  }

  useEffect(() => {
    if (!search_session_id || hasResults) {
      return;
    }
    getSavedSearch();
  }, [search_session_id]);
}

// ===============================================================
export function useTrainsGetSessionMeta({ page_session_id }) {
  const dispatch = useDispatch();
  const history = useHistory();

  const { search_session_id, market_type } = useSelector((state) => ({
    search_session_id: state.SessionIds.TRA.search_session_id,
    market_type: state.TrainSearchForm?.searchPayload.market_type,
  }));

  const onFormUpdate = useCallback((form) => dispatch(trainSearchFormUpdate({ form })), [dispatch]);

  const onChangeSessionIDs = useCallback(
    (value) => dispatch(changeServiceSessionIds("TRA", value)),
    [dispatch],
  );

  const onFailed = useCallback(() => {
    dispatch(trainSearchResultsIdle());
    dispatch(trainSearchResultsFailed());
  }, [dispatch]);

  const pageSessionFetcher = useCallback(
    ({ page_session_id }) => {
      retrieveSearchSessionMetadata({ page_session_id })
        .then((data) => {
          if (!data) {
            actionFailed({
              history,
              onFailed,
              errorMsg: trainsErrorMsgs.searchForm.no_session,
            });
            return;
          }

          const form = _.get(data, "session_data.data.search_form", {});

          onChangeSessionIDs({
            page_session_id,
            search_session_id: _.get(data, "session_data.data.search_session_id", ""),
          });

          onFormUpdate(form);
        })
        .catch(() => {
          actionFailed({
            history,
            onFailed,
            errorMsg: trainsErrorMsgs.searchForm.no_session,
          });
        });
    },
    [dispatch],
  );

  useEffect(() => {
    if (search_session_id) {
      return;
    }
    if (!page_session_id) {
      actionFailed({
        history,
        onFailed,
        errorMsg: trainsErrorMsgs.searchForm.no_session,
      });
      return;
    }
    pageSessionFetcher({ page_session_id });
  }, [search_session_id, page_session_id]);

  return { search_session_id, market_type };
}

export function useGetTraQueryParams() {
  const history = useHistory();
  const location = useLocation();
  const state = location.state;
  try {
    var selectionData = state.encodedData;

    if (selectionData) {
      selectionData = decodeURIComponent(selectionData);
      selectionData = JSON.parse(selectionData);
      return { selectionData };
    }
  } catch (error) {
    history.push("/404/");
    return { selectionData: {} };
  }
}
